<template>
  <div class="app-layout">
    <!-- Full-width navbar at the top -->
    <Navbar class="top-navbar" />

    <div class="main-layout">
      <!-- Sidebar positioned below navbar -->
      <Sidenav class="sidebar-below" :class="{ 'mobile-sidebar-open': mobileSidebarOpen }">
        <!-- Custom Event Banner using slot -->
        <template #event-banner>
          <EventBanner />
        </template>
      </Sidenav>
      <div
          class="sidebar-edge-trigger"
          :style="{
            left: sidebarCollapsed ? 'calc(70px - 6px)' : 'calc(14.625rem - 6px)',
            transition: 'left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }"
          @mousemove="handleEdgeMouseMove"
          @mouseleave="hideEdgeTrigger"
          @click="toggleSidebar"
      >
        <div class="edge-pill" :style="{ top: edgePillY + 'px' }">
          <span class="notch-chevron" :class="{ 'chevron-flipped': sidebarCollapsed }"></span>
        </div>
      </div>
      <!-- Mobile sidebar overlay -->
      <div
          class="mobile-sidebar-overlay"
          :class="{ 'overlay-active': mobileSidebarOpen }"
          @click="closeMobileSidebar"
      ></div>

      <!-- Main content area -->
      <main class="main-content" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <div class="content-wrapper modern-scrollbar">
          <NConfigProvider>
            <RouterView v-slot="{ Component }">
              <KeepAlive include="[ProximaProfiles]">
                <component :is="Component" />
              </KeepAlive>
            </RouterView>
          </NConfigProvider>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { onBeforeMount, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Sidenav from "./Sidenav/index.vue";
import Navbar from "./Navbar/index.vue";
import EventBanner from "./Sidenav/EventBanner.vue";
import {
  getSubscriptionInfo,
  getNotifications,
} from "../composables/subscription";
import { PUBLISHED_PROFILES } from "../composables/profiles/list";
import { NConfigProvider } from 'naive-ui'   
const router = useRouter();
const route = useRoute();
const sidebarCollapsed = ref(false);
const mobileSidebarOpen = ref(false);

const closeMobileSidebar = () => {
  mobileSidebarOpen.value = false;
};
const edgePillY = ref(200);
const edgePillVisible = ref(false);

const handleEdgeMouseMove = (e) => {
  edgePillVisible.value = true;
  const rect = e.currentTarget.getBoundingClientRect();
  edgePillY.value = e.clientY - rect.top - 20; // center pill on cursor
};

const hideEdgeTrigger = () => {
  edgePillVisible.value = false;
};

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  document.dispatchEvent(new CustomEvent('sidebar-toggle', {
    detail: { source: 'notch', collapsed: sidebarCollapsed.value }
  }));
};
const handleMobileSidebarToggle = () => {
  mobileSidebarOpen.value = !mobileSidebarOpen.value;
};

const handleSidebarStateChanged = (event) => {
  sidebarCollapsed.value = event.detail.collapsed;
};

onBeforeMount(async () => {
  await getNotifications();
});

onMounted(() => {
  // Listen for sidebar state changes
  document.addEventListener('sidebar-state-changed', handleSidebarStateChanged);

  // Listen for mobile sidebar toggle events
  document.addEventListener('mobile-sidebar-toggle', handleMobileSidebarToggle);
});

onBeforeUnmount(async () => {
  PUBLISHED_PROFILES.value = {};
  document.removeEventListener('sidebar-state-changed', handleSidebarStateChanged);
  document.removeEventListener('mobile-sidebar-toggle', handleMobileSidebarToggle);
});
</script>

<style scoped>
.sidebar-edge-trigger {
  position: fixed;
  left: calc(14.625rem - 6px);
  top: 70px;
  width: 20px;
  height: calc(100vh - 70px);
  z-index: 1020;
  cursor: pointer;
  transition: left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.sidebar-collapsed .sidebar-edge-trigger,
.sidebar-edge-trigger.is-collapsed {
  left: calc(70px - 6px);
}

.edge-pill {
  position: absolute;
  left: 0;
  width: 24px;
  height: 40px;
  background: #213ce5;
  border-radius: 0 6px 6px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, top 0.05s linear;
}

.sidebar-edge-trigger:hover .edge-pill {
  opacity: 1;
}

.notch-chevron {
  display: block;
  width: 7px;
  height: 7px;
  border-right: 2px solid white;
  border-top: 2px solid white;
  transform: rotate(225deg);
  margin-left: -2px;
  transition: transform 0.3s ease, margin-left 0.3s ease;
}

.notch-chevron.chevron-flipped {
  transform: rotate(45deg);
  margin-left: 2px;
}

@media (max-width: 1199.98px) {
  .sidebar-edge-trigger {
    display: none;
  }
}
.app-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  overflow:hidden;
  background: #f5f7fa;
  position: relative;
}

.top-navbar {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1030;
  margin: 0 !important;
  padding: 0;
  border-radius: 0 !important;
}

.main-layout {
  display: flex;
  margin-top: 70px;
  min-height: calc(100vh - 70px);
  width: 100%;
  position: relative;
  background: white;
}

.sidebar-below {
  position: fixed;
  left: 0;
  top: 70px !important;
  height: calc(100vh - 70px);
  z-index: 1010;
}

.mobile-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1020;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.mobile-sidebar-overlay.overlay-active {
  opacity: 1;
  visibility: visible;
}

.main-content {
  flex: 1;
  margin-left: 14.625rem;
  width: calc(100% - 14.625rem);
  min-height: calc(100vh - 70px);
  overflow: hidden;
  transition: margin-left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
  width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: margin-left, width;
}

.content-wrapper {
  background: white;
  width: 100%;
  height: calc(100vh - 70px);
  overflow-x: hidden;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
  width: calc(100% - 70px);
}

/* Mobile and tablet responsive adjustments */
@media (max-width: 1199.98px) {
  .sidebar-below {
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .main-content {
    margin-left: 0 !important;
    width: 100% !important;
    transition: none;
  }

  .main-content.sidebar-collapsed {
    margin-left: 0 !important;
    width: 100% !important;
  }

  .sidebar-below.mobile-sidebar-open {
    transform: translateX(0);
  }

  .mobile-sidebar-overlay {
    display: block;
  }

  .content-wrapper {
    padding: 1rem;
  }
}

@media (max-width: 767.98px) {
  .main-layout {
    margin-top: 60px;
  }

  .sidebar-below {
    top: 60px !important;
    height: calc(100vh - 60px);
  }

  .main-content {
    min-height: calc(100vh - 60px);
  }

  .content-wrapper {
    height: calc(100vh - 60px);
    padding: 0.75rem;
  }
}

/* Hide mobile overlay on desktop */
@media (min-width: 1200px) {
  .mobile-sidebar-overlay {
    display: none;
  }
}

/* Smooth scrolling */
.content-wrapper {
  scroll-behavior: smooth;
}

/* Remove all rounded borders */
* {
  border-radius: 0 !important;
}
</style>